// Create-stream modal. Shows the streamKey + ingest endpoints ONCE on success.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Copy, Radio } from "lucide-react";
import { ApiError } from "../api/client.js";
import { useCreateStream, useMe } from "../api/hooks.js";
import type { StreamWithSecretsDTO } from "../api/types.js";
import { Button } from "./ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog.js";
import { Input } from "./ui/input.js";
import { Label } from "./ui/label.js";
import { Switch } from "./ui/switch.js";

export interface CreateStreamModalProps {
  open: boolean;
  onClose: () => void;
}

function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-stretch gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs text-foreground">
          {value}
        </code>
        <Button variant="secondary" size="sm" onClick={copy} className="shrink-0">
          {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
          {copied ? t("create.copied") : t("create.copy")}
        </Button>
      </div>
    </div>
  );
}

export function CreateStreamModal({ open, onClose }: CreateStreamModalProps) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const create = useCreateStream();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [scheduledStartAt, setScheduledStartAt] = useState("");
  const [recordEnabled, setRecordEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<StreamWithSecretsDTO | null>(null);

  const canRecord = me?.capabilities.canRecord ?? false;

  const reset = () => {
    setTitle("");
    setSlug("");
    setDescription("");
    setScheduledStartAt("");
    setRecordEnabled(false);
    setError(null);
    setResult(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const body = {
        title: title.trim(),
        ...(slug.trim() ? { slug: slug.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(scheduledStartAt
          ? { scheduledStartAt: new Date(scheduledStartAt).toISOString() }
          : {}),
        ...(canRecord ? { recordEnabled } : {}),
      };
      const res = await create.mutateAsync(body);
      setResult(res);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === "NO_PUBLISH_GRANT") setError(t("create.errNoPublish"));
        else if (e.code === "NO_RECORD_GRANT") setError(t("create.errNoRecord"));
        else setError(e.message);
      } else {
        setError(t("create.errGeneric"));
      }
    }
  };

  const textareaCls =
    "w-full resize-none rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-foreground shadow-inner shadow-black/20 outline-none transition placeholder:text-muted-foreground/90 focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/40";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto scrollbar-thin">
        {result ? (
          <div>
            <DialogHeader>
              <DialogTitle>{t("create.created")}</DialogTitle>
              <DialogDescription className="text-warn">{t("create.keyOnce")}</DialogDescription>
            </DialogHeader>
            <div className="mt-4 flex flex-col gap-4">
              <CopyField label={t("create.streamKey")} value={result.streamKey} />
              {result.ingestEndpoints.map((ep) => (
                <div
                  key={`${ep.nodeId}-${ep.protocol}`}
                  className="rounded-lg border border-white/[0.07] bg-white/[0.02] p-3"
                >
                  <div className="mb-2 text-xs font-medium text-foreground">
                    {ep.nodeName}{" "}
                    <span className="text-muted-foreground">
                      · {ep.region} · {ep.protocol.toUpperCase()}
                    </span>
                  </div>
                  <CopyField label={t("create.obs")} value={ep.obsHint} />
                </div>
              ))}
              <Button asChild variant="primary" className="w-full">
                <Link to={`/watch/${result.slug ?? result.uuid}`} onClick={close}>
                  {t("create.goToStream")}
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Radio className="h-4 w-4 text-primary" />
                {t("create.title")}
              </DialogTitle>
            </DialogHeader>

            {error && (
              <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive-soft">
                {error}
              </div>
            )}

            <div className="mt-4 flex flex-col gap-4">
              <div>
                <Label htmlFor="cs-title">{t("create.fieldTitle")}</Label>
                <Input
                  id="cs-title"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t("create.titlePlaceholder")}
                />
              </div>
              <div>
                <Label htmlFor="cs-slug">{t("create.slug")}</Label>
                <Input
                  id="cs-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="midnight-build"
                />
              </div>
              <div>
                <Label htmlFor="cs-desc">{t("create.description")}</Label>
                <textarea
                  id="cs-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className={textareaCls}
                />
              </div>
              <div>
                <Label htmlFor="cs-sched">{t("create.schedule")}</Label>
                <Input
                  id="cs-sched"
                  type="datetime-local"
                  value={scheduledStartAt}
                  onChange={(e) => setScheduledStartAt(e.target.value)}
                />
              </div>
              {canRecord && (
                <label className="flex items-center justify-between rounded-md border border-white/[0.07] bg-white/[0.02] px-3 py-2.5 text-sm text-foreground">
                  {t("create.record")}
                  <Switch checked={recordEnabled} onCheckedChange={setRecordEnabled} />
                </label>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={close}>
                {t("create.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={create.isPending}
                disabled={!title.trim()}
              >
                {create.isPending ? t("create.creating") : t("create.create")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
